import { Injectable, Injector } from '@angular/core';
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, of, shareReplay, distinctUntilChanged } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { App } from '@capacitor/app';
import { DeviceCacheService } from 'src/mqtt/services/device-cache.service';
import { MqttMessageStorageService } from 'src/mqtt/services/mqtt-message-storage.service';
import { CredentialsService } from '../services/credentials.service';
import { decodeJwtSub } from './jwt-utils';
import {
  classifyHttpStatus,
  needsRefresh,
  nextBackoff,
  parseIotTokenResponse,
  runIotAcquireLadder,
  IotAcquireOutcome,
  IotLadderDeps,
  LadderRungResult,
} from './iot-token-logic';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authenticationSubject: BehaviorSubject<any>;
  private iosTokenSubject: BehaviorSubject<string | undefined>
  private emailAddress: string = '';
  private tokenExpiryDate: Date | null = null;
  private readonly TOKEN_STORAGE_KEY = 'iot_token';
  private readonly TOKEN_EXPIRY_KEY = 'iot_token_expiry';
  private readonly FULLY_AUTHENTICATED_KEY = 'fully_authenticated';
  private currentSession: any = null;
  private cachedSub: string | null = null;
  private readonly COGNITO_SUB_KEY = 'cognito_sub';
  private tokenLoadingPromise: Promise<void> | null = null;
  private iotPassword?: string;
  private refreshTimer: any;
  private retryAttempt = 0;
  /** Single-flight guard so concurrent callers share one acquire attempt. */
  private acquiring?: Promise<IotAcquireOutcome>;
  /** True once every automated recovery path is exhausted (prompt re-auth). */
  private needsReauthSubject = new BehaviorSubject<boolean>(false);
  /** Refresh the IoT token once it is within this window of expiry. */
  private readonly REFRESH_MARGIN_MS = 24 * 60 * 60 * 1000;
  /** Foregrounded re-check cadence; also caps the rotation timer so we never
   *  arm a multi-day setTimeout that a suspended webview would silently drop. */
  private readonly TOKEN_CHECK_INTERVAL_MS = 30 * 60 * 1000;

  // Add new subject to track full authentication state (including PIN/FaceID)
  private fullyAuthenticatedSubject: BehaviorSubject<boolean>;
  
  /** Promise that resolves when session restoration is complete */
  private sessionRestored: Promise<void>;

  public sessionUserAttributes = {};
  public cognitoUser?: CognitoUser;

  constructor(
    private httpClient: HttpClient,
    private deviceCache: DeviceCacheService,
    private mqttMessageStorage: MqttMessageStorageService,
    private credentialsService: CredentialsService,
    private injector: Injector
  ) {
    this.authenticationSubject = new BehaviorSubject<boolean>(false);
    this.iosTokenSubject = new BehaviorSubject<string | undefined>(undefined);
    this.fullyAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    // Load tokens immediately on construction
    this.tokenLoadingPromise = this.loadTokenFromStorage();
    this.loadFullyAuthenticatedFromStorage();
    this.sessionRestored = this.restoreSessionFromStorage();

    // Rotate the IoT token whenever the app returns to the foreground. Mobile
    // webviews suspend JS timers while backgrounded, so a long setTimeout alone
    // never fires; the resume hook guarantees the 7-day token is refreshed well
    // within its window on the next time the resident opens the app.
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.ensureFreshToken().catch(() => { /* failures surface via needsReauth$ */ });
      }
    });
  }

  public refreshSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.cognitoUser) {
        reject(new Error('No Cognito user available'));
        return;
      }
      
      this.cognitoUser.getSession((err: any, session: any) => {
        if (err) {
          console.error('Error refreshing session:', err);
          console.error('Session refresh diagnostics:', this.getSessionRefreshDiagnostics());
          this.currentSession = null;
          reject(err);
        } else if (session && session.isValid()) {
          console.log('Session refreshed successfully');
          this.currentSession = session;
          this.cacheCognitoSub();
          resolve();
        } else {
          console.error('Session refresh diagnostics:', this.getSessionRefreshDiagnostics());
          this.currentSession = null;
          reject(new Error('Invalid session'));
        }
      });
    });
  }

  /**
   * Confirms which half of a session-refresh failure actually occurred:
   * a signed-in user still held in memory vs. the Cognito SDK's own
   * localStorage token cache (a separate store from SecureStoragePlugin).
   * Never throws — diagnostics must not break auth.
   */
  private getSessionRefreshDiagnostics(): { hasCognitoUser: boolean; hasStoredIdToken: boolean } {
    const hasCognitoUser = !!this.cognitoUser;
    let hasStoredIdToken = false;
    try {
      if (this.cognitoUser) {
        const idTokenKey = `CognitoIdentityServiceProvider.${environment.cognitoAppClientId}.${this.cognitoUser.getUsername()}.idToken`;
        hasStoredIdToken = !!localStorage.getItem(idTokenKey);
      }
    } catch {
      // diagnostics must never break auth
    }
    return { hasCognitoUser, hasStoredIdToken };
  }

  public async clearIotToken(): Promise<void> {
    try {
      // Try to remove token, but don't fail if it doesn't exist
      try {
        await SecureStoragePlugin.remove({ key: this.TOKEN_STORAGE_KEY });
      } catch (e: any) {
        if (!e.message?.includes('Item with given key does not exist') && 
            !e.message?.includes('key not found')) {
          console.warn('Error removing IoT token:', e);
        }
      }
      
      // Try to remove expiry, but don't fail if it doesn't exist
      try {
        await SecureStoragePlugin.remove({ key: this.TOKEN_EXPIRY_KEY });
      } catch (e: any) {
        if (!e.message?.includes('Item with given key does not exist') &&
            !e.message?.includes('key not found')) {
          console.warn('Error removing IoT token expiry:', e);
        }
      }

      this.tokenExpiryDate = null;
      this.iosTokenSubject.next(undefined);
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
      
      // Don't clear iotPassword here - we might need it for refresh

      // Clear device caches
      this.deviceCache.clearCache();
      this.mqttMessageStorage.reset();
      
      console.log('Cleared stored IoT token and device caches');
    } catch (error) {
      console.error('Error clearing IoT token:', error);
    }
  }

  private async loadTokenFromStorage() {
    try {
      const tokenRes = await SecureStoragePlugin.get({ key: this.TOKEN_STORAGE_KEY });
      const expiryRes = await SecureStoragePlugin.get({ key: this.TOKEN_EXPIRY_KEY });
      
      if (tokenRes?.value && expiryRes?.value) {
        const expiryDate = new Date(expiryRes.value);
        
        // If token is not expired, use it
        if (expiryDate > new Date()) {
          // console.log('Loaded cached IoT token, expires:', expiryDate);
          this.tokenExpiryDate = expiryDate;
          this.iosTokenSubject.next(tokenRes.value);
          this.scheduleTokenRefresh(expiryDate);
        } else {
          console.log('Cached IoT token expired, clearing it');
          // Clear expired token
          await this.clearIotToken();
        }
      }
    } catch (error) {
      console.log('No cached IoT token found');
    }
  }


  private async loadFullyAuthenticatedFromStorage() {
    try {
      const fullyAuthRes = await SecureStoragePlugin.get({ key: this.FULLY_AUTHENTICATED_KEY });
      
      if (fullyAuthRes?.value === 'true') {
        // console.log('Found cached fully authenticated state');
        // Don't immediately restore the state - wait for Cognito session to be validated
        // The state will be restored when the user's session is validated
        // For now, keep it as false to prevent premature MQTT connections
        this.fullyAuthenticatedSubject.next(false);
      }
    } catch (error) {
      console.log('No cached fully authenticated state found');
    }
  }

  private async saveFullyAuthenticatedToStorage(value: boolean) {
    try {
      await SecureStoragePlugin.set({ key: this.FULLY_AUTHENTICATED_KEY, value: value.toString() });
      // console.log('Fully authenticated state cached:', value);
    } catch (error) {
      console.error('Failed to cache fully authenticated state:', error);
    }
  }

  private async clearFullyAuthenticatedFromStorage() {
    try {
      await SecureStoragePlugin.remove({ key: this.FULLY_AUTHENTICATED_KEY });
      console.log('Cleared stored fully authenticated state');
    } catch (error) {
        console.error('Error clearing fully authenticated state:', error);
    }
  }

  private async saveTokenToStorage(token: string, expiryDate: Date) {
    try {
      await SecureStoragePlugin.set({ key: this.TOKEN_STORAGE_KEY, value: token });
      await SecureStoragePlugin.set({ key: this.TOKEN_EXPIRY_KEY, value: expiryDate.toISOString() });
      console.log('IoT token cached, expires:', expiryDate);
    } catch (error) {
      console.error('Failed to cache IoT token:', error);
    }
  }

  public signOut(): void {
    this.cognitoUser?.signOut(() => {
      this.authenticationSubject.next(false);
      this.fullyAuthenticatedSubject.next(false);
      this.clearFullyAuthenticatedFromStorage();
    });
    // Reset IoT token when signing out
    this.iosTokenSubject.next(undefined);
  }
  public setAuthState(value: boolean) {
    this.authenticationSubject.next(value);
  }

  public async ensureTokenLoaded(): Promise<void> {
    // Wait for token loading to complete if it's in progress
    if (this.tokenLoadingPromise) {
      await this.tokenLoadingPromise;
      this.tokenLoadingPromise = null;
    }
  }

  public getIotToken(): Observable<string | undefined> {
    // If we have a token and it's not expired, return it
    const currentToken = this.iosTokenSubject.getValue();
    if (currentToken && this.tokenExpiryDate && this.tokenExpiryDate > new Date()) {
      const timeLeft = this.tokenExpiryDate.getTime() - Date.now();
      // console.log(`IoT token valid for ${Math.round(timeLeft / 1000 / 60)} minutes`);
      
      // Kick a background refresh once inside the margin. No credential gate:
      // the ladder can self-heal via /refresh even when the stored password is
      // gone. Single-flight in acquireIotToken prevents stampede.
      if (timeLeft < this.REFRESH_MARGIN_MS) {
        this.ensureFreshToken().catch(() => { /* surfaced via needsReauth$ */ });
      }
      return this.iosTokenSubject.asObservable();
    }
    
    // If no token in memory, ensure we've tried loading from storage
    if (!currentToken && this.tokenLoadingPromise) {
      console.log('Token not in memory, waiting for storage load...');
      // Return observable that waits for loading to complete
      return new Observable(observer => {
        this.ensureTokenLoaded().then(() => {
          const subscription = this.iosTokenSubject.subscribe(token => {
            observer.next(token);
            observer.complete();
          });
          
          // Cleanup subscription on unsubscribe
          return () => subscription.unsubscribe();
        });
      });
    }
    
    console.log('No valid IoT token available');
    return this.iosTokenSubject.asObservable();
  }

  public hasIotToken(): Observable<boolean> {
    // Ensure token is loaded first, then subscribe to token changes
    return new Observable<boolean>(observer => {
      let subscription: any;
      
      // Ensure token is loaded first
      this.ensureTokenLoaded().then(() => {
        // Emit initial value based on current state
        const currentToken = this.iosTokenSubject.getValue();
        const hasToken = !!currentToken && !!this.tokenExpiryDate && this.tokenExpiryDate > new Date();
        observer.next(hasToken);
        
        // Then subscribe to token changes - this will emit whenever token changes
        subscription = this.iosTokenSubject.asObservable().pipe(
          map(token => {
            // Check if we have a valid token in memory
            // Use a function to get the current expiry date to ensure we check the latest value
            const hasToken = !!token && !!this.tokenExpiryDate && this.tokenExpiryDate > new Date();
            return hasToken;
          }),
          distinctUntilChanged()
        ).subscribe(hasToken => {
          observer.next(hasToken);
        });
      }).catch(error => {
        console.error('Error ensuring token loaded:', error);
        observer.next(false);
        // Don't complete here - keep the observable alive for future updates
      });
      
      // Return cleanup function
      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    }).pipe(
      distinctUntilChanged(),
      // Add shareReplay to ensure all subscribers get the same value
      shareReplay(1)
    );
  }

  resetPassword(email: string, onSuccessCustom: any, onFailureCustom: any) {
    let poolData = {
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoAppClientId
    };

    let userPool = new CognitoUserPool(poolData);
    let userData = { Username: email, Pool: userPool };
    let cu = new CognitoUser(userData);

    // call forgotPassword on cognitoUser
    cu.forgotPassword({
      onSuccess: function () {
        if (onSuccessCustom) {
          onSuccessCustom();
        }
      },
      onFailure: function (err) {
        if (onFailureCustom) {
          onFailureCustom(err);
        }
        // console.log(err);
      },
    });
  }

  confirmPassword(email: string, verificationCode: string, newPassword: string, onSuccessCustom: any, onFailureCustom: any) {
    let poolData = {
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoAppClientId
    };

    let userPool = new CognitoUserPool(poolData);
    let userData = { Username: email, Pool: userPool };
    let cu = new CognitoUser(userData);

    return new Promise<void>((resolve, reject) => {
      cu.confirmPassword(verificationCode, newPassword, {
        onFailure(err) {
          if (onFailureCustom) {
            onFailureCustom(err);
          }
          reject(err);
        },
        onSuccess() {
          if (onSuccessCustom) {
            onSuccessCustom();
          }
          resolve();
        },
      });
    });
  }

  /**
   * Restore session from storage on app startup
   */
  private async restoreSessionFromStorage(): Promise<void> {
    try {
      // Try to get stored credentials
      const [username, password] = await Promise.all([
        SecureStoragePlugin.get({ key: 'username' }),
        SecureStoragePlugin.get({ key: 'password' })
      ]);
      
      if (username?.value && password?.value) {
        this.emailAddress = username.value;
        this.iotPassword = password.value;
        console.log('Restored credentials from storage');
      }
    } catch (error) {
      // console.log('No stored credentials found during session restore');
    }

    // Restore the cached Cognito sub so telemetry is attributed across cold
    // starts even before a session refresh completes.
    try {
      const sub = await SecureStoragePlugin.get({ key: this.COGNITO_SUB_KEY });
      if (sub?.value) {
        this.cachedSub = sub.value;
      }
    } catch {
      // No cached sub yet (first run or logged out) — expected.
    }
  }

  /**
   * Ensures that session restoration is complete before proceeding.
   * Other services should call this before attempting to use auth tokens.
   */
  public async ensureSessionRestored(): Promise<void> {
    return this.sessionRestored;
  }

  public getAccessToken(): string | null {
    // console.log('Getting access token...');
    if (this.currentSession?.isValid()) {
      const token = this.currentSession.getAccessToken().getJwtToken();
      // console.log('Access token retrieved successfully');
      return token;
    }
    // console.log('No valid session found for access token');
    return null;
  }

  public getIdToken(): string | null {
    if (this.currentSession?.isValid()) {
      return this.currentSession.getIdToken().getJwtToken();
    }
    return null;
  }

  /**
   * Returns a non-expired IdToken, awaiting a Cognito refresh if the cached
   * session is invalid. Throws if no refresh path is available (signed out).
   *
   * Use this anywhere the token is handed to an AWS SDK (Lambda, DynamoDB,
   * Cognito Identity, etc.) — the synchronous getIdToken() can return a
   * token that's about to expire or null when the cached session has gone
   * stale, which silently breaks SDK calls until the user kills the app.
   */
  public async getValidIdToken(): Promise<string> {
    await this.ensureValidSession();
    return this.currentSession.getIdToken().getJwtToken();
  }

  /**
   * Like getValidIdToken() but returns the AccessToken (used for some
   * Bearer-auth Lambda HTTP endpoints).
   */
  public async getValidAccessToken(): Promise<string> {
    await this.ensureValidSession();
    return this.currentSession.getAccessToken().getJwtToken();
  }

  private async ensureValidSession(): Promise<void> {
    if (this.currentSession?.isValid()) return;
    if (!this.cognitoUser) throw new Error('Not signed in');
    await this.refreshSession();
    if (!this.currentSession?.isValid()) {
      throw new Error('Session refresh did not yield a valid session');
    }
  }

  public getSession(): any {
    // console.log('Getting current session...');
    if (this.currentSession?.isValid()) {
      // console.log('Valid session found');
    } else {
      // console.log('No valid session found');
    }
    return this.currentSession;
  }

  public async signIn(emailAddress: string, password: string, cb: any): Promise<void> {
    // console.log('Starting sign in process...');
    this.emailAddress = emailAddress;
    this.iotPassword = password;
    let authenticationDetails = new AuthenticationDetails({
      Username: emailAddress,
      Password: password,
      ValidationData: {
        'aud': environment.cognitoAppClientId
      }
    });

    let poolData = {
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoAppClientId
    };

    // console.log('Creating Cognito user pool...');
    let userPool = new CognitoUserPool(poolData);
    let userData = { Username: emailAddress, Pool: userPool };
    this.cognitoUser = new CognitoUser(userData);
    
    // Wrap the callback to test DynamoDB connection after successful login
    const wrappedCallback = {
      onSuccess: async (session: any) => {
        // console.log('Cognito authentication successful');
        
        // Store the session
        this.currentSession = session;
        
        // Skip unnecessary delays for faster authentication
        if (!session.isValid()) {
          console.error('Cognito session not fully established');
          if (cb && cb.onFailure) {
            cb.onFailure(new Error('Authentication session not fully established'));
          }
          return;
        }

        // Persist the stable Cognito sub for durable telemetry attribution
        this.cacheCognitoSub();

        // Verify we can get the access token
        const accessToken = this.getAccessToken();
        if (!accessToken) {
          console.error('Could not get access token from session');
          if (cb && cb.onFailure) {
            cb.onFailure(new Error('Could not get access token from session'));
          }
          return;
        }
        
        // Initialize IoT connection before calling callback
        // This ensures token is available when user completes PIN/biometric auth
        this.initIotConnection(password).then(() => {
          // console.log('IoT connection initialized successfully');
          // Call the original callback after IoT is ready
          if (cb && cb.onSuccess) {
            cb.onSuccess(session);
          }
        }).catch(error => {
          console.error('IoT initialization failed:', error);
          // Still call the callback even if IoT fails
          // The token can be retried later
          if (cb && cb.onSuccess) {
            cb.onSuccess(session);
          }
        });
      },
      onFailure: (err: any) => {
        console.error('Cognito authentication failed:', err);
        if (cb && cb.onFailure) {
          cb.onFailure(err);
        }
      },
      newPasswordRequired: (userAttributes: any, requiredAttributes: any) => {
        // console.log('New password required for user');
        if (cb && cb.newPasswordRequired) {
          cb.newPasswordRequired(userAttributes, requiredAttributes);
        }
      }
    };

    // console.log('Authenticating user with Cognito...');
    this.cognitoUser.authenticateUser(authenticationDetails, wrappedCallback);
  }

  public async initIotConnection(password: string): Promise<void> {
    // Only request a new token if we don't have a valid one
    const currentToken = this.iosTokenSubject.getValue();
    if (currentToken && this.tokenExpiryDate && this.tokenExpiryDate > new Date()) {
      return;
    }

    // Don't try to get IoT token if user is not authenticated
    if (!this.cognitoUser || !this.emailAddress) {
      console.log('Cannot request IoT token - user not authenticated');
      return;
    }

    this.iotPassword = password;
    await this.acquireIotToken('init');
  }

  /**
   * Observable that turns true once every automated IoT-token recovery path
   * (stored-credential login + Cognito-IdToken refresh) has been exhausted and
   * the resident must sign in again to control their home. Never set on a
   * transient network failure.
   */
  public getNeedsReauth(): Observable<boolean> {
    return this.needsReauthSubject.asObservable().pipe(distinctUntilChanged());
  }

  /** Synchronous snapshot of the needs-reauth flag (for control-flow gates). */
  public getNeedsReauthValue(): boolean {
    return this.needsReauthSubject.value;
  }

  /**
   * Acquire an IoT token via the self-heal ladder, applying side effects
   * (store token, schedule rotation, set/clear needsReauth). Single-flight so
   * concurrent callers (connect + resume + timer) don't stampede the endpoint.
   */
  public acquireIotToken(reason: string): Promise<IotAcquireOutcome> {
    if (this.acquiring) return this.acquiring;
    this.acquiring = this.doAcquireIotToken(reason)
      .finally(() => { this.acquiring = undefined; });
    return this.acquiring;
  }

  private async doAcquireIotToken(reason: string): Promise<IotAcquireOutcome> {
    await this.ensureCredsLoaded();

    const deps: IotLadderDeps = {
      hasStoredCreds: !!(this.emailAddress && this.iotPassword),
      login: () => this.loginForIotToken(this.emailAddress, this.iotPassword as string),
      getIdToken: () => this.getValidIdToken(),
      refresh: (idToken: string) => this.refreshForIotToken(idToken),
      log: (event, details) => this.logAuthEvent(event, { reason, ...(details || {}) }),
    };

    const outcome = await runIotAcquireLadder(deps);

    if (outcome.kind === 'ok') {
      this.applyIotToken(outcome.token, outcome.expiresAt);
      this.logAuthEvent('iot_token_acquired', { reason });
    } else if (outcome.kind === 'permanent') {
      // Exhausted every automated path: stop pretending control works and ask
      // the resident to sign in again. We never force a logout.
      this.setNeedsReauth(true);
      await this.clearIotToken();
      this.logAuthEvent('iot_token_needs_reauth', { reason });
    } else {
      // transient: keep any existing token; a retry is scheduled by the caller.
      this.logAuthEvent('iot_token_transient_failure', { reason });
    }
    return outcome;
  }

  /** Hydrate email/password from the keychain if not already in memory. */
  private async ensureCredsLoaded(): Promise<void> {
    if (this.emailAddress && this.iotPassword) return;
    try {
      const [u, p] = await Promise.all([
        SecureStoragePlugin.get({ key: 'username' }).catch(() => null),
        SecureStoragePlugin.get({ key: 'password' }).catch(() => null),
      ]);
      if (u?.value) this.emailAddress = u.value;
      if (p?.value) this.iotPassword = p.value;
    } catch {
      // Keychain empty (reinstall / reset) — the /refresh rung covers this.
    }
  }

  /** POST /login with stored credentials, classified for the ladder. */
  private loginForIotToken(username: string, password: string): Promise<LadderRungResult> {
    return firstValueFrom(
      this.httpClient.post<{ token?: string }>(environment.iotLoginEndpoint, { username, password }).pipe(
        map((resp): LadderRungResult => {
          const parsed = parseIotTokenResponse(resp);
          return parsed ? { kind: 'ok', ...parsed } : { kind: 'authRejected' };
        }),
        catchError((err: HttpErrorResponse) =>
          of<LadderRungResult>(
            classifyHttpStatus(err?.status) === 'transient' ? { kind: 'transient' } : { kind: 'authRejected' },
          ),
        ),
      ),
    );
  }

  /** POST /refresh with a Cognito IdToken (no password), classified for the ladder. */
  private refreshForIotToken(idToken: string): Promise<LadderRungResult> {
    const url = environment.iotLoginEndpoint.replace(/\/login$/, '/refresh');
    return firstValueFrom(
      this.httpClient.post<{ token?: string }>(url, {}, { headers: { Authorization: `Bearer ${idToken}` } }).pipe(
        map((resp): LadderRungResult => {
          const parsed = parseIotTokenResponse(resp);
          return parsed ? { kind: 'ok', ...parsed } : { kind: 'authRejected' };
        }),
        catchError((err: HttpErrorResponse) =>
          of<LadderRungResult>(
            classifyHttpStatus(err?.status) === 'transient' ? { kind: 'transient' } : { kind: 'authRejected' },
          ),
        ),
      ),
    );
  }

  /** Commit a freshly acquired token and (re)arm rotation. */
  private applyIotToken(token: string, expiresAt: Date): void {
    // Set expiry first, then token, so hasIotToken() emits a consistent state.
    this.tokenExpiryDate = expiresAt;
    this.iosTokenSubject.next(token);
    this.saveTokenToStorage(token, expiresAt);
    this.retryAttempt = 0;
    this.setNeedsReauth(false);
    this.scheduleTokenRefresh(expiresAt);
  }

  private setNeedsReauth(value: boolean): void {
    this.needsReauthSubject.next(value);
  }

  /**
   * Refresh the IoT token if it is missing or within REFRESH_MARGIN_MS of
   * expiry. Called on every app foreground/resume and before each MQTT connect,
   * which is what actually guarantees rotation on mobile (timers don't survive
   * backgrounding).
   */
  public async ensureFreshToken(): Promise<void> {
    await this.ensureTokenLoaded();
    if (needsRefresh(this.tokenExpiryDate, Date.now(), this.REFRESH_MARGIN_MS)) {
      const outcome = await this.acquireIotToken('ensureFresh');
      if (outcome.kind === 'transient') this.scheduleRetry();
    }
  }

  private scheduleTokenRefresh(expiresAt: Date): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const untilMargin = expiresAt.getTime() - Date.now() - this.REFRESH_MARGIN_MS;
    // Never arm a multi-day timer (a backgrounded webview would drop it). Cap at
    // the periodic check interval and re-evaluate when it fires.
    const delay = Math.max(0, Math.min(untilMargin, this.TOKEN_CHECK_INTERVAL_MS));
    this.refreshTimer = setTimeout(() => this.refreshTick(), delay);
  }

  private async refreshTick(): Promise<void> {
    if (needsRefresh(this.tokenExpiryDate, Date.now(), this.REFRESH_MARGIN_MS)) {
      const outcome = await this.acquireIotToken('timer');
      if (outcome.kind === 'transient') this.scheduleRetry();
      // ok -> applyIotToken re-armed the timer; permanent -> stop (needsReauth set)
    } else if (this.tokenExpiryDate) {
      this.scheduleTokenRefresh(this.tokenExpiryDate); // re-arm periodic check
    }
  }

  private scheduleRetry(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.retryAttempt += 1;
    const delay = nextBackoff(this.retryAttempt, 5000, 5 * 60 * 1000);
    this.refreshTimer = setTimeout(() => this.refreshTick(), delay);
  }


  isLoggedIn(): boolean {
    let isAuth = false;
    if (this.cognitoUser != null) {
      this.cognitoUser.getSession((err: any, session: any) => {
        if (err) {
          this.logAuthEvent('isLoggedIn_session_error', { message: err?.message });
          return;
        }
        isAuth = session?.isValid() ?? false;
      });
    }
    return isAuth;
  }

  /**
   * Check and restore authentication state from storage
   * This should be called after Cognito user is available
   */
  public async checkAndRestoreAuthState(): Promise<void> {
    if (!this.cognitoUser) {
      console.log('Cannot restore auth state - no Cognito user');
      return;
    }
    
    try {
      // Check if we have a stored fully authenticated state
      const fullyAuthRes = await SecureStoragePlugin.get({ key: this.FULLY_AUTHENTICATED_KEY });
      
      if (fullyAuthRes?.value === 'true') {
        // Verify Cognito session is still valid
        this.cognitoUser.getSession((err: any, session: any) => {
          if (!err && session?.isValid()) {
            // console.log('Restoring fully authenticated state with valid session');
            this.fullyAuthenticatedSubject.next(true);
          } else {
            console.log('Session invalid, cannot restore fully authenticated state');
            this.clearFullyAuthenticatedFromStorage();
          }
        });
      }
    } catch (error: any) {
      // Handle "Item with given key does not exist" gracefully - this is expected after logout
      if (error?.errorMessage?.includes('does not exist') || error?.message?.includes('does not exist')) {
        // This is expected after logout - the key doesn't exist
        // console.log('No stored auth state found (expected after logout)');
      } else {
        console.log('Error restoring auth state:', error);
      }
    }
  }
  
  async logOut(func?: any) {
    // console.log('Logging out user...');

    // Clear the current session
    this.currentSession = null;
    this.cachedSub = null;

    // Clear all stored credentials (also wipes the cached Cognito sub)
    SecureStoragePlugin.clear();

    // Clear cached username from localStorage
    try {
      localStorage.removeItem('cached_username');
    } catch (e) {
      console.warn('Failed to clear cached username:', e);
    }

    // Clear 2N credentials specifically
    this.credentialsService.clearTwoNCredentials().catch(error => {
      console.warn('Error clearing 2N credentials during logout:', error);
    });

    // Clear full authentication state
    this.fullyAuthenticatedSubject.next(false);
    this.clearFullyAuthenticatedFromStorage();
    
    // Clear device cache (contains cached device states from previous user)
    try {
      this.deviceCache.clearCache();
    } catch (error) {
      console.warn('Error clearing device cache during logout:', error);
    }
    
    // Clear MQTT message storage (contains cached device messages from previous user)
    try {
      this.mqttMessageStorage.reset();
    } catch (error) {
      console.warn('Error clearing MQTT message storage during logout:', error);
    }
    
    // Clear settings preload cache using injector to avoid circular dependency
    try {
      const SettingsPreloadService = (await import('../pages/settings/services/settings-preload.service')).SettingsPreloadService;
      const settingsPreloadService = this.injector.get(SettingsPreloadService);
      await settingsPreloadService.clearCache();
    } catch (error) {
      console.warn('Error clearing settings preload cache:', error);
    }
    
    // Clear user profile cache using injector to avoid circular dependency
    try {
      const UserProfileCacheService = (await import('../services/user-profile-cache.service')).UserProfileCacheService;
      const userProfileCacheService = this.injector.get(UserProfileCacheService);
      await userProfileCacheService.clearCache();
    } catch (error) {
      console.warn('Error clearing user profile cache during logout:', error);
    }
    
    // Clear device registry using injector to avoid circular dependency
    try {
      const DeviceRegistryService = (await import('../services/device-registry.service')).DeviceRegistryService;
      const deviceRegistryService = this.injector.get(DeviceRegistryService);
      await deviceRegistryService.clearRegistry();
    } catch (error) {
      console.warn('Error clearing device registry during logout:', error);
    }
    
    // Clear DynamoDB client using injector to avoid circular dependency
    // This ensures a new client is created with the new user's token
    try {
      const DynamoDBService = (await import('../services/dynamodb.service')).DynamoDBService;
      const dynamoDBService = this.injector.get(DynamoDBService);
      dynamoDBService.clearClient();
    } catch (error) {
      console.warn('Error clearing DynamoDB client during logout:', error);
    }
    
    // Reset UserStateService state using injector to avoid circular dependency
    try {
      const UserStateService = (await import('../shared/services/user-state.class')).UserStateService;
      const userStateService = this.injector.get(UserStateService);
      userStateService.isDataAvailable.next(false);
      userStateService.isNetworkAvailable.next(true);
      userStateService.isApolloAvailable.next(true);
    } catch (error) {
      console.warn('Error resetting UserStateService during logout:', error);
    }

    // Clear analytics user context
    try {
      const AnalyticsService = (await import('../services/analytics.service')).AnalyticsService;
      const analyticsService = this.injector.get(AnalyticsService);
      analyticsService.clearUserContext();
    } catch (error) {
      console.warn('Error clearing analytics user context during logout:', error);
    }

    // Clear feature settings cache (contains earlyAccess and other resident settings)
    try {
      const FeatureSettingsService = (await import('../services/feature-settings.service')).FeatureSettingsService;
      const featureSettingsService = this.injector.get(FeatureSettingsService);
      featureSettingsService.clearCache();
    } catch (error) {
      console.warn('Error clearing feature settings cache during logout:', error);
    }

    // Airtable service doesn't need reset (stateless)

    this.cognitoUser?.signOut(func);
    // Reset IoT token when logging out
    this.iosTokenSubject.next(undefined);
    // console.log('Logout completed');
  }

  getVerificationCodeForAttr(attrName: string, onSuccessCustom: any, onFailureCustom: any) {
    if (!this.cognitoUser) {
      if (onFailureCustom) {
        onFailureCustom("Cognito initialization error");
        return;
      }
      // console.log('Something went wrong. Please try again or contact your caretaker')
      return;
    }

    this.cognitoUser.getAttributeVerificationCode(attrName, {
      onSuccess: function (result) {
        if (onSuccessCustom) {
          onSuccessCustom(result);
        }
        else {
          // console.log('call result: ' + result);
        }
      },
      onFailure: function (err) {
        if (onFailureCustom) {
          onFailureCustom(err);
        } else {
          // console.log(err.message || JSON.stringify(err));
        }
      },
    });
  }

  verifyAttribute(attrName: string, code: string, onSuccessCustom: any, onFailureCustom: any) {
    if (!this.cognitoUser) {
      if (onFailureCustom) {
        onFailureCustom("Cognito initialization error");
        return;
      } else {
        // console.log('Something went wrong. Please try again or contact your caretaker')
      }

      return;
    }

    this.cognitoUser.verifyAttribute(attrName, code, {
      onSuccess: function (result) {
        if (onSuccessCustom) {
          onSuccessCustom(result);
          return;
        }
        // console.log('call result: ' + result);
      },
      onFailure: (err: any) => {
        if (onFailureCustom) {
          onFailureCustom(err);
          return;
        }
        this.logAuthEvent('verifyAttribute_unhandled_error', { message: err?.message });
      },
    });
  }

  updateUserAttribute(attr: any, onSuccessCustom?: any, onFailureCustom?: any) {
    let poolData = {
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoAppClientId,
    };
    let userPool = new CognitoUserPool(poolData);
  
    const currentUser = userPool.getCurrentUser();
  
    if (!currentUser) {
      if (onFailureCustom) {
        onFailureCustom("Cognito initialization error. User is not logged in.");
      } else {
        // console.log('Something went wrong. Please try again or contact your caretaker')
      }
      return;
    }
  
    currentUser.getSession((err: any, session: any) => {
      if (err || !session.isValid()) {
        this.logAuthEvent('updateUserAttribute_invalid_session');
        if (onFailureCustom) {
          onFailureCustom("Session is invalid. User is not authenticated.");
        } else {
          console.log("Session is invalid. User is not authenticated.");
        }
        return;
      }
  
      this.cognitoUser = currentUser;
  
      this.cognitoUser.updateAttributes(attr, (err, result) => {
        if (err) {
          if (onFailureCustom) {
            onFailureCustom(err);
          } else {
            this.logAuthEvent('updateAttributes_unhandled_error', { message: err?.message });
          }
          return;
        }
  
        if (onSuccessCustom) {
          onSuccessCustom(result);
        } else {
          console.log("Call result: " + result);
        }
      });
    });
  }

  getUserAttributes(onError?: any, onSuccess?: any) {
    if (!this.cognitoUser) return;
    this.cognitoUser.getUserAttributes((err, result) => {
      if (err || !result) {
        if (onError) {
          onError(err);
          return;
        }
        // No handler supplied: an invalid session here is recovered by the
        // normal unauthenticated app flow. Log for observability, but never
        // surface a raw alert() to the user.
        this.logAuthEvent('getUserAttributes_unhandled_error', { message: err?.message });
        return;
      }

      // Cache the username in localStorage for instant loading
      const username = result.find((attr: any) => attr.Name === 'given_name')?.Value;
      if (username) {
        try {
          localStorage.setItem('cached_username', username);
        } catch (e) {
          console.warn('Failed to cache username:', e);
        }
      }

      if (onSuccess) {
        onSuccess(result);
        return;
      } else {
        for (let i = 0; i < result.length; i++) {
          // console.log(
          //   'attribute ' + result[i].getName() + ' has value ' + result[i].getValue()
          // );
        }
      }
    });
  }



  /**
   * Get observable for full authentication state (including PIN/FaceID)
   */
  public getFullyAuthenticated(): Observable<boolean> {
    return this.fullyAuthenticatedSubject.asObservable();
  }

  /**
   * Check if user is fully authenticated (including PIN/FaceID)
   */
  public isFullyAuthenticated(): boolean {
    return this.fullyAuthenticatedSubject.value;
  }

  /**
   * Get the Cognito user ID from the ID token. Synchronous — returns null
   * if no cached session is currently valid. For background-recovery-safe
   * lookups, prefer getValidCognitoUserId().
   */
  public getCognitoUserId(): string | null {
    const idToken = this.getIdToken();
    return idToken ? decodeJwtSub(idToken) : null;
  }

  /**
   * Durable user id for TELEMETRY ATTRIBUTION ONLY. Falls back to the cached
   * sub when the live session is temporarily invalid (cold-start race,
   * mid-refresh, expired token). Do NOT use as an auth gate: it can return a
   * sub with no valid session. Auth-gated feature code must use
   * getCognitoUserId() or getValidCognitoUserId() instead.
   */
  public getCachedCognitoUserId(): string | null {
    const idToken = this.getIdToken();
    const liveSub = idToken ? decodeJwtSub(idToken) : null;
    return liveSub ?? this.cachedSub;
  }

  /**
   * Get the Cognito user ID, awaiting a Cognito refresh if the cached
   * session is invalid. Use this when the call site cares whether the
   * user is actually signed in (vs. just whether we have a cached token).
   */
  public async getValidCognitoUserId(): Promise<string> {
    const idToken = await this.getValidIdToken();
    const sub = decodeJwtSub(idToken);
    if (!sub) throw new Error('ID token missing sub claim');
    return sub;
  }

  /**
   * Derive the Cognito sub from the current session, persist it for durable
   * telemetry attribution, and push it to the analytics layer. Called whenever
   * a session is established or refreshed. Never throws — telemetry bookkeeping
   * must not break auth.
   */
  private cacheCognitoSub(): void {
    try {
      const idToken = this.currentSession?.isValid()
        ? this.currentSession.getIdToken().getJwtToken()
        : null;
      const sub = idToken ? decodeJwtSub(idToken) : null;
      if (!sub) {
        return;
      }
      if (sub !== this.cachedSub) {
        this.cachedSub = sub;
        SecureStoragePlugin.set({ key: this.COGNITO_SUB_KEY, value: sub }).catch(() => {});
      }
      this.notifyAnalyticsUserContext(sub);
    } catch {
      // telemetry bookkeeping must never break auth
    }
  }

  private async notifyAnalyticsUserContext(sub: string): Promise<void> {
    try {
      const { AnalyticsService } = await import('../services/analytics.service');
      const analyticsService = this.injector.get(AnalyticsService);
      analyticsService.setUserContext(sub);
    } catch {
      // analytics not ready — the cached-sub fallback still covers attribution
    }
  }

  /**
   * Emit an auth-lifecycle telemetry event to Coralogix via the analytics
   * layer (lazy-resolved to avoid a DI cycle). Never throws.
   */
  private async logAuthEvent(event: string, details?: Record<string, any>): Promise<void> {
    try {
      const { AnalyticsService } = await import('../services/analytics.service');
      this.injector.get(AnalyticsService).logAuthEvent(event, details);
    } catch {
      // telemetry must never break auth
    }
  }

  /**
   * Set the full authentication state (should be called after PIN/FaceID authentication)
   */
  public async setFullyAuthenticated(value: boolean): Promise<void> {
    // console.log('Setting fully authenticated state to:', value);
    this.fullyAuthenticatedSubject.next(value);
    this.saveFullyAuthenticatedToStorage(value);
    
    // If user just became fully authenticated, ensure IoT token is ready
    if (value && this.cognitoUser) {
      // First ensure any pending token loading is complete
      await this.ensureTokenLoaded();
      
      const currentToken = this.iosTokenSubject.getValue();
      if (!currentToken || !this.tokenExpiryDate || this.tokenExpiryDate <= new Date()) {
        console.log('User fully authenticated but IoT token missing or expired, requesting new token');
        
        // Try to get credentials and request a new token
        try {
          const [username, password] = await Promise.all([
            SecureStoragePlugin.get({ key: 'username' }),
            SecureStoragePlugin.get({ key: 'password' })
          ]);
          
          if (username?.value && password?.value) {
            this.emailAddress = username.value; // Ensure email is set
            await this.initIotConnection(password.value);
            console.log('IoT token request initiated after full authentication');
          } else {
            console.error('No stored credentials available to request IoT token');
            // User needs to re-authenticate with username/password
            this.fullyAuthenticatedSubject.next(false);
            this.clearFullyAuthenticatedFromStorage();
            throw new Error('No stored credentials for IoT token request');
          }
        } catch (error) {
          console.error('Error requesting IoT token after full authentication:', error);
        }
      } else {
        // console.log('IoT token is available and valid until:', this.tokenExpiryDate);
        // Force a check to ensure observables are updated
        // The token is already set, so hasIotToken() should return true
        // The MQTT connector service should pick this up via its subscription
      }
    }
  }
}
